import React, { useState, useEffect } from "react";
import { Button } from "../../components/Utilities";
import { apiService } from "../../../services/apiService";
import "./NocModal.css";
import pro from "../../assets/person.svg";
import { Loading } from "../../components/Loading";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      console.error("fileToBase64: Invalid file input:", file);
      reject(new Error("Invalid file input: File object is required"));
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result?.split(",")[1];
      if (!result) {
        reject(new Error("Failed to convert file to base64"));
      } else {
        resolve(result);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const UploadKYCModal = ({
  isOpen,
  onClose,
  carId,
  onSubmitKYC,
  kycData = null,     // When provided → read-only mode
  readOnly = false,   // Toggle view mode
}) => {
  const [activeTab, setActiveTab] = useState("Individual");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [aadharFiles, setAadharFiles] = useState([]);
  const [panFiles, setPanFiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Initialize form in read-only mode
  useEffect(() => {
    if (readOnly && kycData) {
      setActiveTab(kycData.type || "Individual");
      setName(kycData.name || "");
      setEmail(kycData.email || "");
      setAadharFiles([]); // Not needed in view mode
      setPanFiles([]);
    } else if (!readOnly) {
      // Reset for upload mode
      setActiveTab("Individual");
      setName("");
      setEmail("");
      setAadharFiles([]);
      setPanFiles([]);
    }
  }, [readOnly, kycData, isOpen]);

  if (!isOpen) return null;

  const isFormValid =
    !readOnly &&
    name.trim() !== "" &&
    email.trim() !== "" &&
    aadharFiles.length > 0 &&
    panFiles.length > 0;

  const handleTabSwitch = (tab) => {
    if (!readOnly) setActiveTab(tab);
  };

  const handleAadharUpload = (event) => {
    if (readOnly) return;
    const files = Array.from(event.target.files);
    setAadharFiles(files);
    setError(null);
  };

  const handlePanUpload = (event) => {
    if (readOnly) return;
    const files = Array.from(event.target.files);
    setPanFiles(files);
    setError(null);
  };

  const handleSubmit = async () => {
    if (readOnly) return;

    if (!name || !email || aadharFiles.length === 0 || panFiles.length === 0) {
      setError("Please fill in all required fields and upload both Aadhar and PAN cards.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const aadharUrls = await Promise.all(
        aadharFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          const ext = file.type === "application/pdf" ? "pdf" : file.type.includes("jpg") || file.type.includes("jpeg") ? "jpg" : "png";
          const fileName = `aadhar_${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`;
          const uploadData = {
            file: base64,
            folder: "kyc-documents",
            fileName,
            metadata: {
              contentType: file.type,
              originalName: file.name,
              size: file.size,
              documentType: "aadhar",
            },
          };
          const response = await apiService.uploadFile(uploadData);
          return response.data?.publicUrl || "";
        })
      );

      const panUrls = await Promise.all(
        panFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          const ext = file.type === "application/pdf" ? "pdf" : file.type.includes("jpg") || file.type.includes("jpeg") ? "jpg" : "png";
          const fileName = `pan_${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`;
          const uploadData = {
            file: base64,
            folder: "kyc-documents",
            fileName,
            metadata: {
              contentType: file.type,
              originalName: file.name,
              size: file.size,
              documentType: "pan",
            },
          };
          const response = await apiService.uploadFile(uploadData);
          return response.data?.publicUrl || "";
        })
      );

      const kycPayload = {
        carId,
        type: activeTab,
        name,
        email,
        aadharUrls,
        panUrls,
      };

      await apiService.submitKYC(kycPayload);
      onSubmitKYC?.();
      onClose();
    } catch (err) {
      console.error("Error submitting KYC:", err);
      const msg = err.response?.data?.message || err.message || "Failed to submit KYC.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (url) => {
    setSelectedImage(url);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // === PREVIEWS ===
  const aadharPreviews = readOnly
    ? (kycData?.aadharUrls || []).map((url, i) => (
        <img
          key={i}
          src={url}
          alt="Aadhar"
          className="uploadModel-img"
          onClick={() => handleImageClick(url)}
          style={{ cursor: "pointer" }}
        />
      ))
    : aadharFiles
        .filter((f) => f.type.startsWith("image/"))
        .map((f, i) => (
          <img
            key={i}
            src={URL.createObjectURL(f)}
            alt="Aadhar preview"
            className="uploadModel-img"
            onClick={() => handleImageClick(URL.createObjectURL(f))}
            style={{ cursor: "pointer" }}
          />
        ));

  const panPreviews = readOnly
    ? (kycData?.panUrls || []).map((url, i) => (
        <img
          key={i}
          src={url}
          alt="PAN"
          className="uploadModel-img"
          onClick={() => handleImageClick(url)}
          style={{ cursor: "pointer" }}
        />
      ))
    : panFiles
        .filter((f) => f.type.startsWith("image/"))
        .map((f, i) => (
          <img
            key={i}
            src={URL.createObjectURL(f)}
            alt="PAN preview"
            className="uploadModel-img"
            onClick={() => handleImageClick(URL.createObjectURL(f))}
            style={{ cursor: "pointer" }}
          />
        ));

  return (
    <div className="NocModal">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>{readOnly ? "View KYC Details" : "Upload KYC Proof"}</p>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="model-body">
            {loading && <Loading />}
            {error && <p className="error web-error">{error}</p>}

            {/* === RIGHT: FORM === */}
            <div className="right-part">
              <div className="tab-container">
                <p>You are a</p>
                <div className="bio">
                  <button
                    className={`tab-button ${activeTab === "Individual" ? "active" : ""}`}
                    onClick={() => handleTabSwitch("Individual")}
                    disabled={readOnly}
                  >
                    Individual
                  </button>
                  <button
                    className={`tab-button ${activeTab === "Company" ? "active" : ""}`}
                    onClick={() => handleTabSwitch("Company")}
                    disabled={readOnly}
                  >
                    Company
                  </button>
                </div>
              </div>

              <div className="input-group">
                <div>
                  <label className="driver-label">End Customer’s Name*</label>
                  <input
                    type="text"
                    className="driver-input"
                    placeholder="Enter name"
                    value={name}
                    onChange={(e) => !readOnly && setName(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <label className="driver-label">RTO email ID*</label>
                  <input
                    type="email"
                    className="driver-input"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => !readOnly && setEmail(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>

            {/* === LEFT: DOCUMENTS === */}
            <div className="left-part">
              <div className="license-upload-part">
                {/* AADHAR */}
                <div className="upload-aadhar">
                  <div className="profile-pic">
                    {aadharPreviews.length > 0 ? (
                      aadharPreviews
                    ) : (
                      <img src={pro} alt="Default" width="30%" />
                    )}
                  </div>

                  {!readOnly && (
                    <>
                      <input
                        type="file"
                        id={`aadharFileInput-${carId}`}
                        multiple
                        accept="image/*,application/pdf"
                        onChange={handleAadharUpload}
                        style={{ display: "none" }}
                      />
                      <Button
                        className="upload-btn-modal"
                        variant="primary"
                        onClick={() => document.getElementById(`aadharFileInput-${carId}`).click()}
                      >
                        Upload Aadhar Card*
                      </Button>
                    </>
                  )}
                </div>

                {/* PAN */}
                <div className="pan">
                  <div className="profile-pic">
                    {panPreviews.length > 0 ? (
                      panPreviews
                    ) : (
                      <img src={pro} alt="Default" width="30%" />
                    )}
                  </div>

                  {!readOnly && (
                    <>
                      <input
                        type="file"
                        id={`panFileInput-${carId}`}
                        multiple
                        accept="image/*,application/pdf"
                        onChange={handlePanUpload}
                        style={{ display: "none" }}
                      />
                      <Button
                        className="upload-btn-modal"
                        variant="primary"
                        onClick={() => document.getElementById(`panFileInput-${carId}`).click()}
                      >
                        Upload PAN Card*
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Submit Button – Only in Edit Mode */}
              {!readOnly && (
                <div className="submit-btn">
                  <Button
                    className="request-gate-pass-btn"
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading || !isFormValid}
                  >
                    {loading ? "Submitting..." : "Submit KYC"}
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Error & Submit */}
            {error && <p className="error mob-error">{error}</p>}
            {!readOnly && (
              <Button
                className="mobile-request-gate-pass-btn"
                variant="primary"
                onClick={handleSubmit}
                disabled={loading || !isFormValid}
              >
                {loading ? "Submitting..." : "Submit KYC"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseImageModal}>
              ×
            </button>
            <img src={selectedImage} alt="Full size" className="full-size-image" />
          </div>
        </div>
      )}
    </div>
  );
};